import { BreakoutTaskEditorDescriptor, DisplayType, ProgramTask } from '@cibo/core'
import { Markdown } from '@cibo/ui'
import { Box, Button, Stack } from '@mui/material'
import { lazy, ReactElement, ReactNode, Suspense } from 'react'
import { Link } from 'react-router-dom'
import { useProgramEngagementContext } from '../../../context'
import { TaskEditorComponentProps } from '../TaskEditorComponent/types'
import { Aop1236Preview } from '../Tasks/DataEntry/Aop1236Preview'
import { ActionRequestButton } from '../Tasks/DataEntry/ActionRequestButton'
import { BreakoutTaskEditor } from './BreakoutTaskEditor'
import { FieldDetailsTable } from './FieldDetailsTable'
import { InterstitialDisplay } from './InterstitialDisplay'
import { TaskEditorTable } from './TaskEditorTable'

const LazyFileViewer = lazy(() =>
  import('./FileViewer/FileViewer').then(({ FileViewer }) => ({ default: FileViewer }))
)

const DisplayComponents: Record<string, any> = {
  Box,
  Stack,
  ActionRequestButton,
  Aop1236Preview,
}

type DisplayProps = {
  display: DisplayType
  task: ProgramTask
  // This is threaded through to avoid the circular dep
  TaskEditorComponent(props: TaskEditorComponentProps): ReactElement
}

export const Display = ({ display, task, TaskEditorComponent }: DisplayProps) => {
  const { engagement, engagementId } = useProgramEngagementContext()

  const renderDisplayChild = (child: any, key?: string | number): ReactNode => {
    if (typeof child === 'string') {
      return child
    }

    return (
      <Display key={key} display={child} task={task} TaskEditorComponent={TaskEditorComponent} />
    )
  }

  const Component = DisplayComponents[(display as any)?.component]

  if (Component) {
    return (
      <Component {...(display as any).props}>
        {(display as any).children?.map((child: any, index: number) =>
          renderDisplayChild(child, `${(display as any).component}-${index}`)
        )}
      </Component>
    )
  }

  switch (display.type) {
    case 'menuItem':
    case 'navigation':
      return null // these do not render in the body panel

    case 'interstitial':
      return (
        <InterstitialDisplay {...display.params} taskId={task?.id} engagement={engagement.data} />
      )

    case 'message': {
      const { markdown, ...rest } = display.params
      return <Markdown {...rest}>{engagement.data?.replaceCopyTokens(markdown)}</Markdown>
    }

    case 'action': {
      const { label, action } = display.params
      switch (action) {
        case 'exit':
          return (
            <Box alignSelf={'center'}>
              <Button to={'/'} variant="contained" color="primary" component={Link}>
                {label}
              </Button>
            </Box>
          )
      }

      return <span>{`Unknown action display type "${action}"`}</span>
    }

    case 'taskEditor':
      return <TaskEditorComponent task={display.params?.taskConfig || task} {...display} />

    case 'breakoutTaskEditor':
      return (
        <BreakoutTaskEditor
          display={display as BreakoutTaskEditorDescriptor}
          task={task}
          TaskEditorComponent={TaskEditorComponent}
        />
      )

    case 'fieldDetailsTable':
      return <FieldDetailsTable {...display.params} engagement={engagement} />

    case 'taskEditorTable':
      return <TaskEditorTable task={display.params?.taskConfig || task} {...display} />

    case 'fileViewer':
      return (
        <Suspense fallback={null}>
          <LazyFileViewer engagementId={engagementId} params={display.params} />
        </Suspense>
      )
  }

  return (
    <>
      <label>{`Unsupported display:`}</label>
      <pre>{JSON.stringify(display, null, 2)}</pre>
    </>
  )
}
